import React, { useState, useEffect, useContext } from 'react';
import {
  Button,
  FormGroup,
  Input,
  Label,
  Row,
  Col,
  Spinner
} from "reactstrap";
import Select2 from "react-select2-wrapper";
import CreatableSelect from "react-select/creatable";
// @ts-ignore
import ReactBSAlert from "react-bootstrap-sweetalert";
import EmpresaContext from "../../../../contexts/Empresa";
import api from "../../../../services/api";

interface Estagio {
  id?: number;
  estagio_id?: number;
  nome: string;
}

interface Funil {
  id: number | string;
  nome: string;
}

interface CardFormProps {
  card?: any;
  funilId?: number | string;
  estagios?: Estagio[];
  funis?: Funil[];
  tituloExterno?: string;
  onSuccess: () => void;
  onCancel: () => void;
  notify: (type: string, msg: string) => void;
}

const CardForm: React.FC<CardFormProps> = ({
  card,
  funilId,
  estagios,
  funis,
  tituloExterno,
  onSuccess,
  onCancel,
  notify
}) => {
  const { empresaSelecionada } = useContext(EmpresaContext);
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState<React.ReactNode>(null);
  const [colaboradores, setColaboradores] = useState<any[]>([]);

  const [activeFunilId, setActiveFunilId] = useState<string>(funilId ? String(funilId) : '');
  const [activeEstagios, setActiveEstagios] = useState<Estagio[]>(estagios || []);
  const [loadingEstagios, setLoadingEstagios] = useState(false);

  useEffect(() => {
    if (estagios) {
      setActiveEstagios(estagios);
    }
  }, [estagios]);

  const hasPipelineSelector = Array.isArray(funis) && funis.length > 0;

  useEffect(() => {
    if (!activeFunilId || !empresaSelecionada) return;
    if (estagios && estagios.length > 0) return; // Se já recebeu estágios via prop, não busca
    
    let cancelled = false;
    setLoadingEstagios(true);
    api.get(`/common/empresas/${empresaSelecionada.id}/funis/${activeFunilId}/estagios`)
      .then(res => {
        if (cancelled) return;
        const data: Estagio[] = res.data;
        setActiveEstagios(data);
        if (data.length > 0) {
          // só define o default se ainda não há estágio selecionado (novo card ou modo seletor)
          setFormData(prev => ({
            ...prev,
            estagio_id: prev.estagio_id || String(data[0].estagio_id || data[0].id),
          }));
        }
      })
      .catch(() => { if (!cancelled) notify('danger', 'Falha ao carregar estágios.'); })
      .finally(() => { if (!cancelled) setLoadingEstagios(false); });
    return () => { cancelled = true; };
  }, [activeFunilId]);

  const [formData, setFormData] = useState(() => {
    const formatDateForInput = (dateStr?: string) => {
      if (!dateStr) return '';
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return '';
      return d.toISOString().substring(0, 16);
    };

    const parseTags = (raw?: string): string[] =>
      raw ? raw.split(',').map(t => t.trim()).filter(Boolean) : [];

    if (card) {
      return {
        titulo: card.title || card.titulo || '',
        descricao: card.description || card.descricao || '',
        estagio_id: String(card.laneId || card.estagio_id || ''),
        prioridade: card.prioridade || 'normal',
        data_inicio: formatDateForInput(card.data_inicio),
        data_prazo: formatDateForInput(card.data_prazo),
        situacao: card.situacao || 'aberto',
        membros_ids: card.membros ? card.membros.map((m: any) => {
          if (typeof m === 'object' && m !== null) {
            return String(m.user?.id || m.user_id || m.id || '');
          }
          return String(m);
        }) : [] as string[],
        tags: parseTags(card.tags),
      };
    }
    return {
      titulo: '',
      descricao: '',
      estagio_id: (estagios && estagios.length > 0) ? String(estagios[0].estagio_id || estagios[0].id) : '',
      prioridade: 'normal',
      data_inicio: '',
      data_prazo: '',
      situacao: 'aberto',
      membros_ids: [] as string[],
      tags: [] as string[],
    };
  });

  useEffect(() => {
    if (empresaSelecionada) {
      loadColaboradores();
    }
  }, [empresaSelecionada]);

  async function loadColaboradores() {
    try {
      const response = await api.get(`/common/empresas/${empresaSelecionada?.id}/colaboradores?situacaoNaEmpresa=A`);
      setColaboradores(response.data);
    } catch (error) {
      console.error(error);
    }
  }

  const nowMin = (() => {
    const now = new Date();
    now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
    return now.toISOString().substring(0, 16);
  })();

  const resolvedFunilId = activeFunilId || (funilId ? String(funilId) : '');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (formData.data_inicio && formData.data_inicio < nowMin) {
      notify('warning', 'A data de início não pode ser anterior à data atual.');
      return;
    }

    setLoading(true);

    const payload = {
      ...formData,
      titulo: tituloExterno !== undefined ? tituloExterno : formData.titulo,
      funil_id: resolvedFunilId,
      membros_ids: formData.membros_ids.map(Number),
      tags: formData.tags.join(','),
      ...(card?.entidade_tipo ? { entidade_tipo: card.entidade_tipo, entidade_id: card.entidade_id } : {}),
    };

    try {
      if (card && card.id) {
        await api.put(`/common/empresas/${empresaSelecionada?.id}/funis/${resolvedFunilId}/cards/${card.id}`, payload);
        notify('success', 'Card atualizado com sucesso!');
      } else {
        await api.post(`/common/empresas/${empresaSelecionada?.id}/funis/${resolvedFunilId}/cards`, payload);
        notify('success', 'Card criado com sucesso!');
      }
      onSuccess();
    } catch (error) {
      console.error(error);
      notify('danger', 'Erro ao salvar o card.');
    }
    setLoading(false);
  };

  const confirmDelete = async () => {
    setAlert(null);
    setLoading(true);
    try {
      await api.delete(`/common/empresas/${empresaSelecionada?.id}/funis/${resolvedFunilId}/cards/${card.id}`);
      notify('success', 'Card excluído com sucesso!');
      onSuccess();
    } catch (error) {
      console.error(error);
      notify('danger', 'Erro ao excluir o card.');
    }
    setLoading(false);
  };

  const handleDelete = () => {
    setAlert(
      <ReactBSAlert
        warning
        closeOnClickOutside={false}
        title="Excluir card?"
        onConfirm={confirmDelete}
        onCancel={() => setAlert(null)}
        showCancel
        confirmBtnBsStyle="danger"
        confirmBtnText="Sim, excluir"
        cancelBtnBsStyle="link"
        cancelBtnText="Cancelar"
        btnSize=""
      >
        Esta ação não pode ser desfeita.
      </ReactBSAlert>
    );
  };

  return (
    <>
      {alert}
      <form onSubmit={handleSubmit} style={{ display: "contents" }}>
        <div className="modal-body" style={{ overflowY: "auto" }}>

          {hasPipelineSelector && (
            <FormGroup>
              <Label for="pipeline_id">Pipeline *</Label>
              <select
                id="pipeline_id"
                className="form-control"
                value={activeFunilId}
                onChange={e => {
                  setActiveFunilId(e.target.value);
                  setActiveEstagios([]);
                  setFormData(prev => ({ ...prev, estagio_id: '' }));
                }}
                required
              >
                <option value="">Selecione o pipeline...</option>
                {funis!.map(f => (
                  <option key={f.id} value={String(f.id)}>{f.nome}</option>
                ))}
              </select>
            </FormGroup>
          )}

          {tituloExterno === undefined && (
            <FormGroup>
              <Label for="titulo">Título</Label>
              <Input
                type="text"
                id="titulo"
                value={formData.titulo}
                onChange={e => setFormData({ ...formData, titulo: e.target.value })}
                placeholder="Digite o título do card"
                required
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label for="descricao">Descrição</Label>
            <Input
              type="textarea"
              id="descricao"
              value={formData.descricao}
              onChange={e => setFormData({ ...formData, descricao: e.target.value })}
              placeholder="Descreva a tarefa ou objetivo"
              rows={3}
            />
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="estagio_id">Estágio</Label>
                <Select2
                  className="form-control"
                  value={formData.estagio_id}
                  onSelect={(e: any) => setFormData({ ...formData, estagio_id: e.target.value })}
                  data={activeEstagios.map(e => ({ id: String(e.estagio_id || e.id), text: e.nome }))}
                  options={{ placeholder: hasPipelineSelector && !activeFunilId ? 'Selecione um pipeline primeiro' : 'Selecione o estágio' }}
                  disabled={loadingEstagios}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="prioridade">Prioridade</Label>
                <Select2
                  className="form-control"
                  value={formData.prioridade}
                  onSelect={(e: any) => setFormData({ ...formData, prioridade: e.target.value })}
                  data={[
                    { id: 'baixa', text: 'Baixa' },
                    { id: 'normal', text: 'Normal' },
                    { id: 'alta', text: 'Alta' },
                    { id: 'urgente', text: 'Urgente' }
                  ]}
                />
              </FormGroup>
            </Col>
          </Row>

          <FormGroup>
            <Label for="membros">Membros da Equipe</Label>
            <Select2
              key={colaboradores.length}
              className="form-control"
              multiple
              value={colaboradores.length > 0 ? formData.membros_ids : []}
              onChange={(e: any) => {
                const values = Array.from(e.target.selectedOptions).map((opt: any) => opt.value);
                setFormData({ ...formData, membros_ids: values });
              }}
              data={colaboradores.map(c => ({ id: String(c.user_id || c.id), text: c.name }))}
              options={{
                placeholder: 'Selecione os membros',
                multiple: true
              }}
            />
          </FormGroup>

          <FormGroup>
            <Label>Tags</Label>
            <CreatableSelect
              isMulti
              placeholder="Adicionar tags..."
              value={formData.tags.map(t => ({ value: t, label: t }))}
              onChange={(selected: any) =>
                setFormData({
                  ...formData,
                  tags: selected ? (selected.map((s: any) => s.value as string) as string[]) : [],
                })
              }
              styles={{
                control: (base: any) => ({
                  ...base,
                  minHeight: 'calc(2.75rem + 2px)',
                  borderColor: '#DEE2E6',
                }),
              }}
            />
          </FormGroup>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="data_inicio">Data de Início</Label>
                <Input
                  type="datetime-local"
                  id="data_inicio"
                  value={formData.data_inicio}
                  min={nowMin}
                  onChange={e => setFormData({ ...formData, data_inicio: e.target.value })}
                />
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="data_prazo">Prazo (Deadline)</Label>
                <Input
                  type="datetime-local"
                  id="data_prazo"
                  value={formData.data_prazo}
                  onChange={e => setFormData({ ...formData, data_prazo: e.target.value })}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="situacao">Situação</Label>
                <Select2
                  className="form-control"
                  value={formData.situacao}
                  onSelect={(e: any) => setFormData({ ...formData, situacao: e.target.value })}
                  data={[
                    { id: 'aberto', text: 'Aberto' },
                    { id: 'concluido', text: 'Concluído' },
                    { id: 'cancelado', text: 'Cancelado' },
                    { id: 'arquivado', text: 'Arquivado' }
                  ]}
                />
              </FormGroup>
            </Col>
          </Row>
        </div>
        <div className="modal-footer d-flex justify-content-between">
          <div>
            {card && card.id && (
              <Button color="danger" outline onClick={handleDelete} disabled={loading}>
                Excluir
              </Button>
            )}
          </div>
          <div>
            <Button color="secondary" onClick={onCancel} disabled={loading}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={loading}>
              {loading ? <Spinner size="sm" /> : 'Salvar'}
            </Button>
          </div>
        </div>
      </form>
    </>
  );
};

export default CardForm;
